import React, { useState, useCallback } from 'react';
import {
  VStack, HStack, Box, Text, Button, Progress, Badge,
  Table, Thead, Tbody, Tr, Th, Td, Icon, Checkbox,
  Select, Alert, AlertIcon, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  useColorModeValue, Tooltip,
} from '@chakra-ui/react';
import { FiCheckCircle, FiAlertCircle, FiDownload, FiPlay, FiCreditCard } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getOrders } from 'services/woo';
import _ from 'lodash';
import BatchUsersOrderCreate from './BatchUsersOrderCreate';

const ALL_MONTHS = [
  { value: '01', label: 'Ene' }, { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' }, { value: '04', label: 'Abr' },
  { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' }, { value: '08', label: 'Ago' },
  { value: '09', label: 'Sep' }, { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' }, { value: '12', label: 'Dic' },
];

const CURRENT_YEAR = new Date().getFullYear();

const PaymentAudit = ({ customers, onFetchAll = null, totalCustomers = 0 }) => {
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [fromMonth, setFromMonth] = useState('03');
  const [toMonth, setToMonth] = useState('12');
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentName, setCurrentName] = useState('');
  const [results, setResults] = useState(null);
  // Objetos completos de los clientes escaneados (para pasar a BatchUsersOrderCreate)
  const [scannedCustomers, setScannedCustomers] = useState([]);
  // Selección
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Modal de cobro
  const [showChargeModal, setShowChargeModal] = useState(false);

  const bgOk = useColorModeValue('green.50', 'green.900');
  const bgSelected = useColorModeValue('blue.50', 'blue.900');
  const bgRow = useColorModeValue('white', 'gray.800');
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const summaryBg = useColorModeValue('gray.50', 'gray.700');

  const selectedMonths = ALL_MONTHS.filter(m => m.value >= fromMonth && m.value <= toMonth);
  const expectedCount = selectedMonths.length;

  // ── Escaneo ──────────────────────────────────────────────────────────────
  const runScan = useCallback(async () => {
    setScanning(true);
    setProgress(0);
    setResults(null);
    setSelectedIds(new Set());

    let customersToScan = customers;
    if (onFetchAll && totalCustomers > customers.length) {
      setCurrentName('Cargando todos los usuarios...');
      customersToScan = await onFetchAll();
    }

    if (!customersToScan?.length) {
      setScanning(false);
      setResults([]);
      return;
    }

    setScannedCustomers(customersToScan);
    const rows = [];

    for (let i = 0; i < customersToScan.length; i++) {
      const customer = customersToScan[i];
      setCurrentName(`${customer.first_name} ${customer.last_name}`);
      setProgress(Math.round((i / customersToScan.length) * 100));

      try {
        const resp = await getOrders({ customer: customer.id, per_page: 100, page: 1 });
        const orders = resp.data || resp || [];

        const activeOrders = orders.filter(o => o.status !== 'trash' && o.status !== 'cancelled');

        const chargedMonths = new Set();
        activeOrders.forEach(o => {
          const mes = o.meta_data?.find(m => m.key === 'mes')?.value;
          if (mes && mes.startsWith(year + '-')) {
            const [, mm] = mes.split('-');
            chargedMonths.add(mm.padStart(2, '0'));
          }
        });

        const missingMonths = selectedMonths.filter(m => !chargedMonths.has(m.value));
        const chargedCount = selectedMonths.filter(m => chargedMonths.has(m.value)).length;

        rows.push({
          id: customer.id,
          name: `${customer.first_name} ${customer.last_name}`,
          username: customer.username,
          group: _.find(customer.meta_data, { key: 'group_name' })?.value || '—',
          suscription: _.find(customer.meta_data, { key: 'suscription_name' })?.value || '—',
          chargedCount,
          missingMonths,
          chargedMonths: [...chargedMonths],
        });
      } catch {
        rows.push({
          id: customer.id,
          name: `${customer.first_name} ${customer.last_name}`,
          username: customer.username,
          group: '—', suscription: '—',
          chargedCount: 0,
          missingMonths: selectedMonths,
          chargedMonths: [],
          error: true,
        });
      }
    }

    setProgress(100);
    setCurrentName('');
    setScanning(false);
    setResults(rows);
  }, [customers, onFetchAll, totalCustomers, year, selectedMonths]);

  // ── Selección ─────────────────────────────────────────────────────────────
  const sortedResults = results
    ? [...results].sort((a, b) => b.missingMonths.length - a.missingMonths.length)
    : [];

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(sortedResults.map(r => r.id)));
  const selectIncomplete = () =>
    setSelectedIds(new Set(sortedResults.filter(r => r.missingMonths.length > 0).map(r => r.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const allChecked = sortedResults.length > 0 && selectedIds.size === sortedResults.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < sortedResults.length;

  // Clientes completos seleccionados (para BatchUsersOrderCreate)
  const selectedCustomers = scannedCustomers.filter(c => selectedIds.has(c.id));

  // ── Exportar ──────────────────────────────────────────────────────────────
  const exportXLSX = () => {
    if (!results) return;
    const monthHeaders = selectedMonths.map(m => `${m.label} ${year}`);
    const data = results.map(r => {
      const row = {
        Nombre: r.name, RUT: r.username, Grupo: r.group, Mensualidad: r.suscription,
        'Meses cargados': r.chargedCount, 'Meses faltantes': r.missingMonths.length,
      };
      selectedMonths.forEach((m, i) => {
        row[monthHeaders[i]] = r.chargedMonths.includes(m.value) ? '✓' : '✗';
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Auditoría');
    XLSX.writeFile(wb, `auditoria-cobros-${year}.xlsx`);
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = results ? {
    total: results.length,
    completos: results.filter(r => r.missingMonths.length === 0).length,
    incompletos: results.filter(r => r.missingMonths.length > 0 && r.missingMonths.length < expectedCount).length,
    sinCobros: results.filter(r => r.missingMonths.length === expectedCount).length,
  } : null;

  const displayTotal = totalCustomers > (customers?.length || 0) ? totalCustomers : (customers?.length || 0);

  return (
    <VStack spacing={5} align="stretch">

      {/* Configuración */}
      <HStack spacing={4} wrap="wrap">
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Año</Text>
          <Select size="sm" value={year} onChange={e => setYear(e.target.value)} w="90px">
            {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Desde</Text>
          <Select size="sm" value={fromMonth} onChange={e => setFromMonth(e.target.value)} w="100px">
            {ALL_MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Hasta</Text>
          <Select size="sm" value={toMonth} onChange={e => setToMonth(e.target.value)} w="100px">
            {ALL_MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </Box>
        <Box alignSelf="flex-end">
          <Text fontSize="xs" color="gray.500" mb={1}>{displayTotal} usuarios · {expectedCount} meses</Text>
        </Box>
      </HStack>

      {/* Botón escaneo */}
      <Button
        colorScheme="blue"
        leftIcon={<Icon as={FiPlay} />}
        onClick={runScan}
        isLoading={scanning}
        loadingText={`Escaneando ${currentName}...`}
        isDisabled={!customers?.length || expectedCount === 0}
      >
        Iniciar Escaneo
      </Button>

      {scanning && (
        <Box>
          <Progress value={progress} colorScheme="blue" borderRadius="md" hasStripe isAnimated />
          <Text fontSize="xs" color="gray.500" mt={1}>{progress}% — {currentName}</Text>
        </Box>
      )}

      {/* Resumen + acciones */}
      {stats && (
        <>
          <HStack spacing={4} p={3} bg={summaryBg} borderRadius="md" wrap="wrap">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="bold">{stats.total}</Text>
              <Text fontSize="xs" color="gray.500">Escaneados</Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="bold" color="green.500">{stats.completos}</Text>
              <Text fontSize="xs" color="gray.500">Completos</Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="bold" color="orange.500">{stats.incompletos}</Text>
              <Text fontSize="xs" color="gray.500">Incompletos</Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="bold" color="red.500">{stats.sinCobros}</Text>
              <Text fontSize="xs" color="gray.500">Sin cobros</Text>
            </Box>
            <Button ml="auto" size="sm" leftIcon={<Icon as={FiDownload} />} colorScheme="green" variant="outline" onClick={exportXLSX}>
              Exportar Excel
            </Button>
          </HStack>

          {/* Barra de selección rápida */}
          <HStack spacing={2} wrap="wrap">
            <Text fontSize="xs" color="gray.500">Seleccionar:</Text>
            <Button size="xs" variant="outline" onClick={selectAll}>Todos ({stats.total})</Button>
            <Button size="xs" variant="outline" colorScheme="orange" onClick={selectIncomplete}>
              Incompletos ({stats.incompletos + stats.sinCobros})
            </Button>
            {selectedIds.size > 0 && (
              <Button size="xs" variant="ghost" onClick={clearSelection}>Limpiar</Button>
            )}
          </HStack>

          {/* Barra de acción cuando hay selección */}
          {selectedIds.size > 0 && (
            <HStack
              p={3} bg="blue.50" borderRadius="md" border="1px solid"
              borderColor="blue.200" justify="space-between" wrap="wrap"
            >
              <Text fontSize="sm" fontWeight="semibold" color="blue.800">
                {selectedIds.size} usuario{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}
              </Text>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<Icon as={FiCreditCard} />}
                onClick={() => setShowChargeModal(true)}
              >
                Cobrar ({selectedIds.size})
              </Button>
            </HStack>
          )}

          <Divider />

          {/* Tabla */}
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr bg={theadBg}>
                  <Th w="36px" px={2}>
                    <Checkbox
                      isChecked={allChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={e => e.target.checked ? selectAll() : clearSelection()}
                      size="sm"
                      colorScheme="blue"
                    />
                  </Th>
                  <Th fontSize="xs">Alumno</Th>
                  <Th fontSize="xs">Mensualidad</Th>
                  <Th fontSize="xs">Cargados</Th>
                  {selectedMonths.map(m => (
                    <Th key={m.value} fontSize="xs" textAlign="center" px={1}>{m.label}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {sortedResults.map(r => {
                  const isComplete = r.missingMonths.length === 0;
                  const isChecked = selectedIds.has(r.id);
                  return (
                    <Tr
                      key={r.id}
                      bg={isChecked ? bgSelected : isComplete ? bgOk : bgRow}
                      cursor="pointer"
                      onClick={() => toggleId(r.id)}
                      _hover={{ opacity: 0.85 }}
                    >
                      <Td px={2} onClick={e => e.stopPropagation()}>
                        <Checkbox
                          isChecked={isChecked}
                          onChange={() => toggleId(r.id)}
                          size="sm"
                          colorScheme="blue"
                        />
                      </Td>
                      <Td py={1}>
                        <VStack align="flex-start" spacing={0}>
                          <Text fontSize="xs" fontWeight="medium" noOfLines={1}>{r.name}</Text>
                          <Text fontSize="xs" color="gray.500">{r.username}</Text>
                        </VStack>
                      </Td>
                      <Td py={1}>
                        <Text fontSize="xs" noOfLines={1} maxW="140px">{r.suscription}</Text>
                      </Td>
                      <Td py={1}>
                        <Badge
                          colorScheme={r.chargedCount === expectedCount ? 'green' : r.chargedCount === 0 ? 'red' : 'orange'}
                          fontSize="xs"
                        >
                          {r.chargedCount}/{expectedCount}
                        </Badge>
                      </Td>
                      {selectedMonths.map(m => {
                        const has = r.chargedMonths.includes(m.value);
                        return (
                          <Td key={m.value} textAlign="center" px={1} py={1}>
                            <Tooltip label={has ? `${m.label} cargado` : `${m.label} faltante`}>
                              <span>
                                <Icon
                                  as={has ? FiCheckCircle : FiAlertCircle}
                                  color={has ? 'green.400' : 'red.400'}
                                  boxSize={3.5}
                                />
                              </span>
                            </Tooltip>
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}

      {results && results.length === 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />No se encontraron resultados.
        </Alert>
      )}

      {/* Modal de cobro — reutiliza BatchUsersOrderCreate */}
      <Modal isOpen={showChargeModal} onClose={() => setShowChargeModal(false)} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Generar Cobro — {selectedIds.size} usuario{selectedIds.size !== 1 ? 's' : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <BatchUsersOrderCreate
              customers={selectedCustomers}
              onOrderCreated={() => setShowChargeModal(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default PaymentAudit;
